import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  PackageOpen, Play, Download, Trash2, RefreshCw,
  ShieldCheck, Copy, Check, AlertTriangle, Database,
  FileCode, Cloud, FolderArchive, Lock,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (b = 0) => {
  if (!b) return '—';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), u.length - 1);
  return `${(b / 1024 ** i).toFixed(1)} ${u[i]}`;
};

const OptionToggle = ({ id, icon: Icon, label, desc, checked, onChange }) => (
  <label
    htmlFor={id}
    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all select-none
      ${checked ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
  >
    <input id={id} type="checkbox" checked={checked} onChange={onChange} className="mt-1 accent-indigo-600" data-testid={`migration-opt-${id}`} />
    <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${checked ? 'text-indigo-600' : 'text-gray-400'}`} />
    <div>
      <p className={`font-semibold text-sm ${checked ? 'text-indigo-800' : 'text-gray-700'}`}>{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
    </div>
  </label>
);

const PasswordBox = ({ password, onCopied }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      onCopied?.();
      setTimeout(() => setCopied(false), 3000);
    });
  };
  return (
    <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4" data-testid="migration-password-box">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-4 h-4 text-amber-600" />
        <span className="font-bold text-amber-800 text-sm">Your Decryption Password — shown once, copy it now</span>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-white border border-amber-300 rounded-lg px-3 py-2 font-mono text-base tracking-widest text-amber-900 break-all select-all">
          {password}
        </code>
        <Button size="sm" variant="outline" onClick={copy} data-testid="migration-copy-password-btn">
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <p className="text-xs text-amber-700 mt-2">
        <AlertTriangle className="w-3 h-3 inline mr-1" />
        This password will NOT be shown again. Save it in your password manager before downloading.
      </p>
    </div>
  );
};

const AdminMigrationTool = () => {
  const authHeader = useCallback(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const [options, setOptions] = useState({ include_db: true, include_env: true, include_s3: true, include_source: true });
  const [jobs, setJobs] = useState([]);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [password, setPassword] = useState(null);  // shown once, stored in component state
  const [downloading, setDownloading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin-settings/migration/list`, { headers: authHeader() });
      setJobs(res.data?.jobs || []);
    } catch { /* ignore */ }
  }, [authHeader]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Poll active job
  useEffect(() => {
    if (!activeJobId) { clearInterval(pollRef.current); return; }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/api/admin-settings/migration/status/${activeJobId}`, { headers: authHeader() });
        const job = res.data;
        setActiveJob(job);
        // Capture password the FIRST time it arrives
        if (job.password && !password) setPassword(job.password);
        if (job.status === 'ready' || job.status === 'failed') {
          clearInterval(pollRef.current);
          loadJobs();
        }
      } catch { clearInterval(pollRef.current); }
    };
    pollRef.current = setInterval(poll, 3000);
    poll();
    return () => clearInterval(pollRef.current);
  }, [activeJobId, authHeader, loadJobs, password]);

  const handleStart = async () => {
    setStarting(true);
    setPassword(null);
    setActiveJob(null);
    try {
      const res = await axios.post(`${API}/api/admin-settings/migration/start`, options, { headers: authHeader() });
      setActiveJobId(res.data.job_id);
      toast({ title: 'Migration started', description: 'Preparing your bundle in the background…' });
    } catch (err) {
      toast({ title: 'Failed to start', description: err?.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setStarting(false); }
  };

  const handleDownload = async () => {
    if (!activeJobId) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/admin-settings/migration/download/${activeJobId}`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = activeJob?.file_name || 'migration_bundle.zip';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Download started' });
    } catch (err) {
      toast({ title: 'Download failed', description: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setDownloading(false); }
  };

  const handleCleanup = async (jobId) => {
    setCleaning(true);
    try {
      await axios.delete(`${API}/api/admin-settings/migration/cleanup/${jobId}`, { headers: authHeader() });
      if (jobId === activeJobId) { setActiveJobId(null); setActiveJob(null); setPassword(null); }
      await loadJobs();
      toast({ title: 'Bundle cleaned up', description: 'Files removed from server.' });
    } catch (err) {
      toast({ title: 'Cleanup failed', description: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setCleaning(false); }
  };

  const isReady = activeJob?.status === 'ready';
  const isProcessing = activeJob?.status === 'processing';
  const isFailed = activeJob?.status === 'failed';

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-migration-tool">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-indigo-100 rounded-xl"><PackageOpen className="w-6 h-6 text-indigo-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Migration Export Tool</h1>
          <p className="text-gray-500 text-sm">Super admin only — create a full encrypted migration bundle to move this platform to another server.</p>
        </div>
      </div>

      {/* Security notice */}
      <div className="flex items-start gap-3 p-4 bg-slate-900 rounded-xl border border-slate-700 text-slate-300 text-sm">
        <ShieldCheck className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-white mb-1">Secured access</p>
          <p>This tool is only accessible when enabled via Feature Flags. Turn the flag OFF after use to remove it from the settings menu entirely.</p>
        </div>
      </div>

      {/* Options */}
      <Card data-testid="migration-options-card">
        <CardHeader>
          <CardTitle>Step 1 — Select what to include</CardTitle>
          <CardDescription>All options are recommended for a complete server migration.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <OptionToggle id="include_db" icon={Database} label="MongoDB Database" desc="Full JSON export of every collection"
              checked={options.include_db} onChange={e => setOptions(o => ({ ...o, include_db: e.target.checked }))} />
            <OptionToggle id="include_env" icon={FileCode} label="Environment Files" desc="backend/.env & frontend/.env"
              checked={options.include_env} onChange={e => setOptions(o => ({ ...o, include_env: e.target.checked }))} />
            <OptionToggle id="include_s3" icon={Cloud} label="S3 / iDrive Files" desc="All objects from your cloud bucket"
              checked={options.include_s3} onChange={e => setOptions(o => ({ ...o, include_s3: e.target.checked }))} />
            <OptionToggle id="include_source" icon={FolderArchive} label="Source Code" desc="/app directory (excl. node_modules, build)"
              checked={options.include_source} onChange={e => setOptions(o => ({ ...o, include_source: e.target.checked }))} />
          </div>
          <div className="mt-4">
            <Button onClick={handleStart} disabled={starting || isProcessing} data-testid="migration-start-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Play className="w-4 h-4 mr-2" />
              {starting ? 'Starting…' : isProcessing ? 'Preparing…' : 'Prepare Migration Bundle'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Progress */}
      {activeJob && (
        <Card data-testid="migration-progress-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Step 2 — Bundle Status
              {isProcessing && <RefreshCw className="w-4 h-4 animate-spin text-indigo-500" />}
              {isReady && <Check className="w-4 h-4 text-green-600" />}
              {isFailed && <AlertTriangle className="w-4 h-4 text-red-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress bar */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm text-gray-600">
                <span>{activeJob.progress_message}</span>
                <span>{activeJob.progress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isFailed ? 'bg-red-500' : isReady ? 'bg-green-500' : 'bg-indigo-500'}`}
                  style={{ width: `${activeJob.progress}%` }}
                />
              </div>
            </div>

            {isFailed && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="migration-error-msg">
                <AlertTriangle className="w-4 h-4 inline mr-1" /> {activeJob.error || 'Migration job failed.'}
              </div>
            )}

            {/* Password — shown once */}
            {isReady && password && <PasswordBox password={password} />}
            {isReady && !password && activeJob.password_already_shown && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700" data-testid="migration-password-hidden-notice">
                <Lock className="w-4 h-4 inline mr-1" /> Password was already shown and has been cleared from the server. If you didn't copy it, run a new bundle.
              </div>
            )}

            {/* Download & meta */}
            {isReady && (
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={handleDownload} disabled={downloading} data-testid="migration-download-btn"
                  className="bg-green-600 hover:bg-green-700 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  {downloading ? 'Downloading…' : `Download Bundle (${formatBytes(activeJob.file_size)})`}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleCleanup(activeJobId)} disabled={cleaning} data-testid="migration-cleanup-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> {cleaning ? 'Cleaning…' : 'Clean Up'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Previous jobs */}
      {jobs.length > 0 && (
        <Card data-testid="migration-history-card">
          <CardHeader>
            <CardTitle>Previous Bundles</CardTitle>
            <CardDescription>Bundles are stored on the server until manually cleaned up.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {jobs.map(j => (
                <div key={j.job_id} className="flex items-center justify-between p-3 rounded-lg border bg-gray-50" data-testid={`migration-job-row-${j.job_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{j.file_name}</p>
                    <p className="text-xs text-gray-500">{new Date(j.created_at).toLocaleString()} · {formatBytes(j.file_size)} · {j.status}</p>
                  </div>
                  <div className="flex gap-2 ml-3 flex-shrink-0">
                    {j.status === 'ready' && (
                      <Button size="sm" variant="outline" onClick={() => { setActiveJobId(j.job_id); setActiveJob(j); }} data-testid={`migration-reopen-${j.job_id}`}>
                        <Download className="w-3 h-3 mr-1" /> Open
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => handleCleanup(j.job_id)} disabled={cleaning} data-testid={`migration-delete-${j.job_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminMigrationTool;
